import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BarChart3, Plus, Save, Trash2 } from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import { usePassbook } from '@/shared/lib/passbook';
import { canAccessSuperAdmin } from '@/shared/lib/navigation';
import { PAGE_HELP } from '@/shared/config/page-help';
import { PageHeader } from '@/shared/components/page-header';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { Card, CardContent, CardHeader } from '@/shared/ui/card';
import type { MetabaseConfig } from '@/shared/types';
import { updateMetabaseConfig } from '../api/use-metabase-config';

/** Um item novo em branco (ainda sem dashboard preenchido). */
function emptyItem(): MetabaseConfig {
  return { dashboard: '', multi: false, lojaIndividual: '', aba: '' };
}

/** Edita a lista de dashboards do Metabase exibidos no Painel/BI da loja. Só super-admin. */
export function MetabaseConfigPage() {
  const { user } = useAuth();
  const { idPassbook, passbook } = usePassbook();
  const queryClient = useQueryClient();
  const [items, setItems] = useState<MetabaseConfig[]>(() => passbook.metabase ?? []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems(passbook.metabase ?? []);
  }, [passbook]);

  if (!user || !canAccessSuperAdmin(user.email)) {
    return <Navigate to={`/loja/${idPassbook}`} replace />;
  }

  const patch = (index: number, patchValue: Partial<MetabaseConfig>) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patchValue } : item)));
  };

  const addItem = () => {
    setItems((prev) => [...prev, emptyItem()]);
  };

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const invalid = items.some(
      (item) => item.dashboard === '' || item.dashboard == null || Number.isNaN(Number(item.dashboard)),
    );
    if (invalid) {
      toast.error('Preencha um dashboard numérico válido em todos os itens antes de salvar.');
      return;
    }
    const payload = items.map((item) => ({
      dashboard: Number(item.dashboard),
      multi: item.multi || undefined,
      lojaIndividual: item.lojaIndividual?.trim() || undefined,
      aba: item.aba?.trim() || undefined,
    }));
    setSaving(true);
    try {
      await updateMetabaseConfig(idPassbook, payload);
      await queryClient.invalidateQueries({ queryKey: ['passbook', idPassbook] });
      toast.success('Metabase salvo.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível salvar o Metabase.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Metabase"
        help={PAGE_HELP.metabaseConfig}
        description={`${passbook.organizationName} — dashboards exibidos no Painel/BI`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="secondary" onClick={addItem}>
              <Plus className="size-4" />
              Adicionar dashboard
            </Button>
            <Button onClick={() => void handleSave()} loading={saving}>
              <Save className="size-4" />
              Salvar
            </Button>
          </div>
        }
      />

      {items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
            <BarChart3 className="size-6" />
            Nenhum dashboard configurado. Clique em “Adicionar dashboard” para criar o primeiro.
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {items.map((item, index) => (
            <Card key={index}>
              <CardHeader className="flex-row items-center justify-between gap-2">
                <span className="text-sm font-semibold text-muted-foreground">
                  Dashboard {index + 1}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Remover dashboard"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => removeItem(index)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label htmlFor={`dashboard-${index}`}>Dashboard (ID)</Label>
                  <Input
                    id={`dashboard-${index}`}
                    type="number"
                    inputMode="numeric"
                    value={item.dashboard}
                    onChange={(e) => patch(index, { dashboard: e.target.value })}
                    placeholder="Ex.: 20"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor={`aba-${index}`}>Aba (opcional)</Label>
                  <Input
                    id={`aba-${index}`}
                    value={item.aba ?? ''}
                    onChange={(e) => patch(index, { aba: e.target.value })}
                    placeholder="Ex.: Fidelidade"
                  />
                </div>
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <Label htmlFor={`loja-${index}`}>Loja individual (opcional)</Label>
                  <Input
                    id={`loja-${index}`}
                    value={item.lojaIndividual ?? ''}
                    onChange={(e) => patch(index, { lojaIndividual: e.target.value })}
                    placeholder="Ex.: Loja Exemplo"
                  />
                </div>
                <div className="flex items-center gap-3 sm:col-span-2">
                  <Switch
                    id={`multi-${index}`}
                    checked={Boolean(item.multi)}
                    onCheckedChange={(checked) => patch(index, { multi: checked })}
                  />
                  <Label htmlFor={`multi-${index}`}>Multi</Label>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
